import { Flex, Text, palette } from "@minervaproject/fds";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";

import { useArraySelector } from "hooks/redux_hooks";
import { useUsersProfileProperties } from "hooks/use_user_properties";
import {
  MonitoringEventTypeType,
  PubsubStatsMonitoringEventType,
} from "shared/types/monitoring_event";
import { selectMonitoringEventsByType } from "store/selectors/monitoring";
import { longDisplayName } from "utils/user";

type ChartDataType = {
  id: string;
  user: string;
  rtt: number;
};

const tickStyles = {
  fontFamily: "'acumin-pro', sans-serif",
  fontSize: "12px",
  fill: palette.blackTint40,
};
const CHART_MIN_HEIGHT = 250;

const RTTChartTooltip = ({ active, payload }: TooltipProps<number, string>) => {
  if (!active || !payload) return null;
  const item = payload[0].payload;

  return (
    <Flex
      direction="column"
      bgColor="blackTint20"
      p={2}
      style={{ borderRadius: "3px", maxWidth: "200px" }}
    >
      <Text kind="body-xs" color="white">
        Median RTT to pubsub server: {item.rtt} ms
      </Text>
      <Text kind="body-xs" color="white">
        User: {item.user}
      </Text>
    </Flex>
  );
};

const RTTBarChart = () => {
  const users = useUsersProfileProperties();
  const pubsubStats = useArraySelector(
    selectMonitoringEventsByType(MonitoringEventTypeType.PUBSUB_STATS),
    ["id"],
  ) as PubsubStatsMonitoringEventType[];
  const dataObject: { [key: string]: ChartDataType } = {};
  pubsubStats.forEach((event) => {
    const rtt = event.contextData.pingRTT?.median || 0;
    if (rtt > 0) {
      dataObject[event.userId] = {
        id: event.userId,
        user: longDisplayName(users[event.userId]),
        rtt,
      };
    }
  });
  const data = Object.values(dataObject);
  const labelFormatter = (value: string) => {
    const totalWidth =
      document.getElementsByClassName("recharts-cartesian-grid")[0] &&
      document.getElementsByClassName("recharts-cartesian-grid")[0].getBoundingClientRect().width;
    const barWidth = totalWidth / data.length;
    const maxCharacters = barWidth / 6 - 3; // 6 = approximate length of a character, subtract another 3 for the ellipsis
    if (value.length < maxCharacters) return value;
    return `${value.substring(0, maxCharacters)}...`;
  };

  if (!data.length) return null;

  return (
    <>
      <div>
        <Text kind="body-l" mb={2} style={{ textAlign: "center" }}>
          Latest median round-trip ping time to the pubsub server per user
        </Text>
      </div>
      <ResponsiveContainer width="95%" height="95%" minHeight={CHART_MIN_HEIGHT}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="user"
            interval={0}
            stroke={palette.blackTint90}
            tick={tickStyles}
            tickFormatter={labelFormatter}
            tickLine={false}
          />
          <YAxis
            allowDecimals={false}
            axisLine={false}
            tick={tickStyles}
            tickLine={false}
            domain={[0, "dataMax"]}
            label={{ value: "Median RTT (ms)", position: "insideLeft", angle: -90, dy: 50 }}
          />
          <Tooltip content={<RTTChartTooltip />} cursor={false} isAnimationActive={false} />
          <Bar dataKey="rtt" isAnimationActive={false}>
            {data.map((item) => (
              <Cell key={`cell-${item.id}`} fill={palette.greenTint20} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};

export { RTTChartTooltip, RTTBarChart };
